import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Clock, Loader2, MessageSquare, ThumbsUp, ThumbsDown, RotateCcw, Play } from "lucide-react";
import playerjs from "player.js";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function fmtTs(sec) {
  if (sec === null || sec === undefined || isNaN(sec) || sec < 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const APPROVAL_META = {
  approved: { text: "Approved", color: "text-emerald-400 border-emerald-900/50 bg-emerald-950/30", icon: ThumbsUp },
  rejected: { text: "Rejected", color: "text-red-400 border-red-900/50 bg-red-950/30", icon: ThumbsDown },
  "changes-requested": { text: "Changes requested", color: "text-amber-400 border-amber-900/50 bg-amber-950/30", icon: RotateCcw },
};

export default function AssetChecklist() {
  const { assetId } = useParams();
  const { user } = useAuth();
  const canEdit = ["admin", "editor"].includes(user?.role);

  const [asset, setAsset] = useState(null);
  const [embed, setEmbed] = useState("");
  const [comments, setComments] = useState([]);
  const [hideResolved, setHideResolved] = useState(false);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);

  // Wire up player.js so we can seek/play the Bunny iframe from outside
  const onIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const player = new playerjs.Player(iframe);
      playerRef.current = player;
    } catch (e) {
      // player.js init failed — jump-to will fall back to ?t= reload
    }
  };

  useEffect(() => {
    if (!assetId) return;
    api.get(`/assets/${assetId}`).then((r) => setAsset(r.data));
    api.get(`/assets/${assetId}/embed`).then((r) => setEmbed(r.data.embed_url)).catch(() => {});
    loadComments();
  }, [assetId]);

  const loadComments = async () => {
    try {
      const r = await api.get(`/assets/${assetId}/comments`);
      setComments(r.data);
    } catch (e) { /* no comments yet */ }
  };

  const toggleResolved = async (commentId, currentlyResolved) => {
    // Optimistic update so the UI feels instant on a long list
    setComments((prev) => prev.map((c) => c.id === commentId ? { ...c, resolved: !currentlyResolved } : c));
    try {
      await api.patch(`/comments/${commentId}/resolve`, { resolved: !currentlyResolved });
      await loadComments();
    } catch (e) {
      // revert on error
      setComments((prev) => prev.map((c) => c.id === commentId ? { ...c, resolved: currentlyResolved } : c));
      alert(e.response?.data?.detail || "Could not update");
    }
  };

  // Group comments by timestamp bucket (every 10s) for easier scanning
  const groups = useMemo(() => {
    const topLevel = comments.filter((c) => !c.parent_comment_id);
    const visible = hideResolved ? topLevel.filter((c) => !c.resolved) : topLevel;
    const sorted = [...visible].sort((a, b) => {
      const at = a.timestamp_sec ?? 1e9;
      const bt = b.timestamp_sec ?? 1e9;
      if (at !== bt) return at - bt;
      return new Date(a.created_at) - new Date(b.created_at);
    });
    const out = [];
    let lastBucket = null;
    for (const c of sorted) {
      const bucket = c.timestamp_sec === null || c.timestamp_sec === undefined ? "general" : Math.floor(c.timestamp_sec / 10) * 10;
      if (bucket !== lastBucket) {
        out.push({ bucket, items: [] });
        lastBucket = bucket;
      }
      out[out.length - 1].items.push(c);
    }
    return out;
  }, [comments, hideResolved]);

  const total = comments.filter((c) => !c.parent_comment_id).length;
  const done = comments.filter((c) => c.resolved && !c.parent_comment_id).length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  const jumpTo = (sec) => {
    if (sec === null || sec === undefined) return;
    // Preferred: player.js wrapper around the Bunny iframe
    if (playerRef.current) {
      try {
        playerRef.current.setCurrentTime(sec);
        playerRef.current.play();
        iframeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      } catch (e) { /* fall through */ }
    }
    // Fallback: reload the iframe with ?t= so Bunny seeks on load
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const url = new URL(iframe.src);
      url.searchParams.set("autoplay", "true");
      url.searchParams.set("t", Math.floor(sec));
      iframe.src = url.toString();
      iframe.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) { /* iframe not ready */ }
  };

  if (!canEdit) {
    return (
      <div className="min-h-screen bg-[#09090b] text-zinc-300 flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-lg font-heading mb-2">Not available</div>
          <p className="text-sm text-zinc-500">The checklist workspace is admin/editor only.</p>
          <Link to="/" className="inline-block mt-4 text-xs text-[var(--brand-accent)] hover:underline">← Back to dashboard</Link>
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-accent)]" />
      </div>
    );
  }

  return (
    <div data-testid="asset-checklist-page" className="min-h-screen bg-[#09090b] text-zinc-200 grain-overlay">
      {/* Sticky header */}
      <header className="sticky top-0 z-20 bg-[#09090b]/95 backdrop-blur-md border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-4">
          <Link
            to="/"
            data-testid="checklist-back-link"
            className="text-xs text-zinc-500 hover:text-[var(--brand-accent)] flex items-center gap-1.5 transition shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Review checklist</div>
            <h1 className="font-heading text-base md:text-lg font-semibold tracking-tight truncate">{asset.title}</h1>
          </div>
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Progress</div>
            <div className="w-40 h-1.5 bg-zinc-900 rounded-full overflow-hidden">
              <div
                data-testid="checklist-progress-bar"
                className="h-full bg-[var(--brand-accent)] transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="text-xs text-zinc-300 tabular-nums w-16">{done} / {total}</div>
          </div>
          <button
            onClick={() => setHideResolved((p) => !p)}
            data-testid="checklist-hide-resolved-btn"
            className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition shrink-0 ${hideResolved ? "text-[var(--brand-accent)] border-[var(--brand-accent)]/40 bg-[var(--brand-accent)]/10" : "text-zinc-500 border-zinc-800 hover:border-zinc-600"}`}
          >
            {hideResolved ? "Showing open" : "Hide done"}
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8 grid lg:grid-cols-[1.4fr_1fr] gap-8">
        {/* Player */}
        <div className="lg:sticky lg:top-[88px] self-start">
          <div className="aspect-video bg-black rounded-xl overflow-hidden border border-zinc-900">
            {embed ? (
              <iframe
                ref={iframeRef}
                src={embed}
                onLoad={onIframeLoad}
                className="w-full h-full"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                title={asset.title}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-700"><Loader2 className="w-5 h-5 animate-spin" /></div>
            )}
          </div>
          {asset.description && (
            <p className="text-sm text-zinc-400 mt-4 leading-relaxed">{asset.description}</p>
          )}
        </div>

        {/* Checklist */}
        <div className="space-y-5">
          {total === 0 ? (
            <div className="text-center py-16 text-zinc-500 border border-dashed border-zinc-900 rounded-xl">
              <MessageSquare className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
              <div className="text-sm">No feedback yet for this asset.</div>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-emerald-400 border border-emerald-900/40 bg-emerald-950/20 rounded-xl">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2" />
              <div className="text-sm font-medium">All feedback resolved 🎬</div>
            </div>
          ) : (
            groups.map((g) => (
              <section key={g.bucket} data-testid={`checklist-group-${g.bucket}`}>
                <div className="flex items-center gap-2 mb-2">
                  {g.bucket === "general" ? (
                    <>
                      <MessageSquare className="w-3.5 h-3.5 text-[var(--brand-accent)]" />
                      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">General comments</span>
                    </>
                  ) : (
                    <>
                      <Clock className="w-3.5 h-3.5 text-[var(--brand-accent)]" />
                      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                        {fmtTs(g.bucket)}{fmtTs(g.bucket + 10) ? `–${fmtTs(g.bucket + 10)}` : ""}
                      </span>
                    </>
                  )}
                </div>
                <ul className="space-y-2">
                  {g.items.map((c) => (
                    <ChecklistItem
                      key={c.id}
                      c={c}
                      replies={comments.filter((r) => r.parent_comment_id === c.id)}
                      onToggle={() => toggleResolved(c.id, c.resolved)}
                      onJump={() => jumpTo(c.timestamp_sec)}
                    />
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </main>
    </div>
  );
}

function ChecklistItem({ c, replies = [], onToggle, onJump }) {
  const approval = c.approval ? APPROVAL_META[c.approval] : null;
  const ts = fmtTs(c.timestamp_sec);
  return (
    <li>
      <div
        data-testid={`checklist-item-${c.id}`}
        className={`relative flex items-start gap-3 p-3 rounded-lg border transition ${c.resolved ? "bg-zinc-950/40 border-zinc-900 opacity-60" : "bg-zinc-950/70 border-zinc-800 hover:border-[var(--brand-accent)]/30"}`}
      >
        <button
          onClick={onToggle}
          data-testid={`checklist-toggle-${c.id}`}
          className={`shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition ${c.resolved ? "bg-emerald-500/20 border-emerald-500" : "border-zinc-700 hover:border-[var(--brand-accent)]"}`}
          aria-label={c.resolved ? "Mark as open" : "Mark as done"}
          title={c.resolved ? "Mark as open" : "Mark as done"}
        >
          {c.resolved ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Circle className="w-3 h-3 text-transparent" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-medium text-zinc-200 truncate">{c.commenter_name}</span>
            {ts && (
              <button
                onClick={onJump}
                data-testid={`checklist-jump-${c.id}`}
                className="inline-flex items-center gap-1 text-[10px] text-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/10 px-1.5 py-0.5 rounded transition"
                title="Jump to this moment in the video"
              >
                <Play className="w-2.5 h-2.5 fill-[var(--brand-accent)]" /> {ts}
              </button>
            )}
            {approval && (
              <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${approval.color}`}>
                <approval.icon className="w-2.5 h-2.5" /> {approval.text}
              </span>
            )}
          </div>
          {c.text && <p className={`text-sm leading-relaxed ${c.resolved ? "line-through text-zinc-500" : "text-zinc-300"}`}>{c.text}</p>}
          {replies.length > 0 && (
            <div className="mt-2 pl-3 border-l-2 border-zinc-800 space-y-1.5">
              {replies.map((r) => (
                <div key={r.id} className="text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className={`font-medium ${r.is_admin_reply ? "text-[var(--brand-accent)]" : "text-zinc-300"}`}>{r.commenter_name}</span>
                    {r.is_admin_reply && (
                      <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)]/80 bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 px-1 py-0.5 rounded-full">Team</span>
                    )}
                    <span className="text-[10px] text-zinc-600">{timeAgo(r.created_at)}</span>
                  </div>
                  <p className="text-zinc-400 mt-0.5 leading-relaxed">{r.text}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 text-[10px] text-zinc-600 mt-1.5">
            <span>{timeAgo(c.created_at)}</span>
            {c.resolved && c.resolved_by_name && (
              <span className="text-emerald-400/70">
                Resolved by {c.resolved_by_name} {c.resolved_at && `· ${timeAgo(c.resolved_at)}`}
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
